import { DIMENSION_TITLES, MEASURE_TITLES } from '../analytics'
import { MEASURE_DEFINITION_BY_TITLE } from '../analyticsDefinitions'
import { EXPLORER_TYPE } from '../renderTypes'
import { CustomizationField } from '~/types/customization'
import { useCustomizationStore } from '~/stores/customization'
import { getTableViewsFilters } from '~/services/explore'
import { FilterOperator, OrderValue } from '~/types/cube'
import { SourceType } from '~/types/sources'
import {
  DashboardModuleType,
  type DashboardConfig,
  type ModuleConfig
} from '~/types/dashboards'

export const createRetentionDashboardConfig = (): DashboardConfig => {
  const customizationStore = useCustomizationStore()
  const employeeGroupDimension = customizationStore.getCustomizationByField(
    CustomizationField.EMPLOYEE_GROUP
  )

  const isTrialPeriodDataAvailable =
    customizationStore.isTrialPeriodDataAvailable

  return [
    {
      titleKey: 'dashboard.retention.departureMetrics',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.retention.departures.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE,
                dimension: employeeGroupDimension,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              withoutTrend: true,
              titleKey: 'dashboard.retention.timeStay.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_STAY,
                dimension: employeeGroupDimension,
                type: EXPLORER_TYPE.BAR
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_STAY
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_STAY
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_STAY]
            }
          }
        },
        {
          moduleType: DashboardModuleType.DISTRIBUTION,
          options: {
            config: {
              titleKey: 'dashboard.retention.departureReasons.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE,
                type: EXPLORER_TYPE.DOUGHNUT,
                dimension: DIMENSION_TITLES.EMPLOYEE_DEPARTURE_REASON
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE],
              dimensions: [DIMENSION_TITLES.EMPLOYEE_DEPARTURE_REASON],
              filters: [
                {
                  member: DIMENSION_TITLES.EMPLOYEE_DEPARTURE_REASON,
                  operator: FilterOperator.SET,
                  values: []
                }
              ],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.retention.attrition.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ATTRITION,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ATTRITION
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[MEASURE_TITLES.EMPLOYEE_ATTRITION]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_ATTRITION]
            }
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.retention.lastLeftEmployees.title',
              columnOrder: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_DEPARTURE_DATE
              ],
              columnsTranslationPrefix:
                'dashboard.retention.lastLeftEmployees.columns',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE],
              dimensions: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_DEPARTURE_DATE
              ],
              filters: getTableViewsFilters(
                MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE
              ),
              order: [
                [DIMENSION_TITLES.EMPLOYEE_DEPARTURE_DATE, OrderValue.DESC]
              ],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.retention.regrettedAttrition.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ATTRITION_REGRETTED,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ATTRITION_REGRETTED
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_ATTRITION_REGRETTED
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_ATTRITION_REGRETTED]
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.moreData',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.retention.ratioDepartureRegretted.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_RATIO_DEPARTURE_REGRETTED,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_RATIO_DEPARTURE_REGRETTED
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_RATIO_DEPARTURE_REGRETTED]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.retention.turnover.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_TURNOVER,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_TURNOVER
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[MEASURE_TITLES.EMPLOYEE_TURNOVER]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_TURNOVER]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.retention.retention.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_RETENTION,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_RETENTION
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[MEASURE_TITLES.EMPLOYEE_RETENTION]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_RETENTION]
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.retention.trialPeriodMetrics',
      content: [
        ...(isTrialPeriodDataAvailable
          ? ([
              {
                moduleType: DashboardModuleType.DOUBLE,
                options: {
                  config: {
                    titleKey: 'dashboard.retention.trialPeriod.title',
                    firstSubtitleKey:
                      'dashboard.retention.trialPeriod.totalOngoing',
                    secondSubtitleKey:
                      'dashboard.retention.trialPeriod.ratioTrialPeriod',
                    exploreLink: {
                      measure: MEASURE_TITLES.EMPLOYEE_COUNT_END_TRIAL_PERIOD,
                      type: EXPLORER_TYPE.LIST
                    },
                    infoContent: {
                      definition:
                        'dashboard.retention.trialPeriod.info.definition',
                      source: SourceType.HRIS,
                      insight: 'dashboard.retention.trialPeriod.info.insight'
                    }
                  },
                  firstQuery: {
                    measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END_TRIAL_PERIOD]
                  },
                  secondQuery: {
                    measures: [MEASURE_TITLES.EMPLOYEE_RATIO_TRIAL_PERIOD]
                  }
                }
              },
              {
                moduleType: DashboardModuleType.DISTRIBUTION,
                options: {
                  config: {
                    titleKey: 'dashboard.retention.trialPeriodEvents.title',
                    exploreLink: {
                      measure: MEASURE_TITLES.EMPLOYEE_COUNT_TRIAL_PERIOD_EVENT,
                      type: EXPLORER_TYPE.DOUGHNUT,
                      dimension:
                        DIMENSION_TITLES.EMPLOYEE_TRIAL_PERIOD_EVENT_TYPE
                    }
                  },
                  query: {
                    measures: [
                      MEASURE_TITLES.EMPLOYEE_COUNT_TRIAL_PERIOD_EVENT
                    ],
                    dimensions: [
                      DIMENSION_TITLES.EMPLOYEE_TRIAL_PERIOD_EVENT_TYPE
                    ],
                    limit: 5
                  }
                }
              }
            ] satisfies ModuleConfig[])
          : ([
              {
                moduleType: DashboardModuleType.DOUBLE,
                options: {
                  config: {
                    titleKey: 'dashboard.retention.firstSixMonths.title',
                    firstSubtitleKey:
                      'dashboard.retention.firstSixMonths.countEnd',
                    secondSubtitleKey:
                      'dashboard.retention.firstSixMonths.ratioFirstSixMonths',
                    exploreLink: {
                      measure:
                        MEASURE_TITLES.EMPLOYEE_COUNT_END_FIRST_SIX_MONTHS,
                      type: EXPLORER_TYPE.LIST
                    },
                    infoContent: {
                      definition:
                        'dashboard.retention.firstSixMonths.info.definition',
                      source: SourceType.HRIS,
                      insight: 'dashboard.retention.firstSixMonths.info.insight'
                    }
                  },
                  firstQuery: {
                    measures: [
                      MEASURE_TITLES.EMPLOYEE_COUNT_END_FIRST_SIX_MONTHS
                    ]
                  },
                  secondQuery: {
                    measures: [MEASURE_TITLES.EMPLOYEE_RATIO_FIRST_SIX_MONTHS]
                  }
                }
              },
              {
                moduleType: DashboardModuleType.SINGLE,
                options: {
                  config: {
                    titleKey:
                      'dashboard.retention.firstSixMonthsNonTransformationRate.title',
                    exploreLink: {
                      measure:
                        MEASURE_TITLES.EMPLOYEE_FIRST_SIX_MONTHS_NON_TRANSFORMATION_RATE,
                      type: EXPLORER_TYPE.KPI
                    },
                    benchmarkLink: {
                      measure:
                        MEASURE_TITLES.EMPLOYEE_FIRST_SIX_MONTHS_NON_TRANSFORMATION_RATE
                    },
                    infoContent:
                      MEASURE_DEFINITION_BY_TITLE[
                        MEASURE_TITLES
                          .EMPLOYEE_FIRST_SIX_MONTHS_NON_TRANSFORMATION_RATE
                      ]
                  },
                  query: {
                    measures: [
                      MEASURE_TITLES.EMPLOYEE_FIRST_SIX_MONTHS_NON_TRANSFORMATION_RATE
                    ]
                  }
                }
              }
            ] satisfies ModuleConfig[])),
        {
          moduleType: DashboardModuleType.DOUBLE,
          options: {
            config: {
              titleKey: 'dashboard.retention.firstYear.title',
              firstSubtitleKey:
                'dashboard.retention.firstYear.countEndFirstYear',
              secondSubtitleKey: 'dashboard.retention.firstYear.ratioFirstYear',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_END_FIRST_YEAR,
                type: EXPLORER_TYPE.LIST
              },
              infoContent: {
                definition: 'dashboard.retention.firstYear.info.definition',
                source: SourceType.HRIS,
                insight: 'dashboard.retention.firstYear.info.insight'
              }
            },
            firstQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END_FIRST_YEAR]
            },
            secondQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_RATIO_FIRST_YEAR]
            }
          }
        },
        ...(isTrialPeriodDataAvailable
          ? [
              {
                moduleType: DashboardModuleType.SINGLE,
                options: {
                  config: {
                    titleKey:
                      'dashboard.retention.trialPeriodNonTransformationRate.title',
                    exploreLink: {
                      measure:
                        MEASURE_TITLES.EMPLOYEE_TRIAL_PERIOD_NON_TRANSFORMATION_RATE,
                      type: EXPLORER_TYPE.KPI
                    },
                    benchmarkLink: {
                      measure:
                        MEASURE_TITLES.EMPLOYEE_TRIAL_PERIOD_NON_TRANSFORMATION_RATE
                    },
                    infoContent:
                      MEASURE_DEFINITION_BY_TITLE[
                        MEASURE_TITLES
                          .EMPLOYEE_TRIAL_PERIOD_NON_TRANSFORMATION_RATE
                      ]
                  },
                  query: {
                    measures: [
                      MEASURE_TITLES.EMPLOYEE_TRIAL_PERIOD_NON_TRANSFORMATION_RATE
                    ]
                  }
                }
              } satisfies ModuleConfig
            ]
          : []),
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey:
                'dashboard.retention.firstYearNonTransformationRate.title',
              exploreLink: {
                measure:
                  MEASURE_TITLES.EMPLOYEE_FIRST_YEAR_NON_TRANSFORMATION_RATE,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure:
                  MEASURE_TITLES.EMPLOYEE_FIRST_YEAR_NON_TRANSFORMATION_RATE
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_FIRST_YEAR_NON_TRANSFORMATION_RATE
                ]
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_FIRST_YEAR_NON_TRANSFORMATION_RATE
              ]
            }
          }
        }
      ]
    }
  ]
}
